// Weapon firing and projectile simulation.
// Conforms to GDD §4, §5.7, §6.3.1, and the Gate 1 integration contract.

import { cfg } from '../config.js';
import type {
  Enemy,
  EnemyDef,
  Player,
  Projectile,
  Vec2,
  WeaponDef,
  WeaponLevel,
  WeaponState,
} from '../types.js';
import type { WorldState } from './state.js';
import { SpatialHash } from './spatial-hash.js';
import { circleCircleSweep, wrapArena, wrappedDelta } from './collision.js';
import { applyDamageToPlayer, playerCombatStats, resolveHit } from './combat.js';
import { evaluateTarget, predictAim } from './targeting.js';
import { getUpgradeMods } from './upgrade-system.js';
import { getTraitMods } from './loadout.js';
import { getTurretWeaponBuffs } from './turret.js';
import { getSerenityMods } from './serenity.js';
import { killEnemy } from './kill.js';
import weaponsData from '../content/weapons.json';
import enemiesData from '../content/enemies.json';

const WEAPON_DEFS = (weaponsData as { version: string; entries: WeaponDef[] }).entries;
const ENEMY_DEFS = (enemiesData as { version: string; entries: { hitbox_radius: number }[] }).entries;

/** Largest enemy hitbox in content: the hit broadphase must reach it. */
const MAX_ENEMY_HITBOX = Math.max(...ENEMY_DEFS.map((d) => d.hitbox_radius));

export interface HomingSpec {
  turn_rate: number;
  seek_radius: number;
}

// Homing specs by weapon id (spec-arsenal seeker behavior), precomputed once.
const HOMING_SPECS = new Map<string, HomingSpec>();
for (const d of WEAPON_DEFS) {
  if (d.homing) HOMING_SPECS.set(d.id, d.homing);
}

// Wave specs by weapon id (boon-system-v1 wavy bullet trait), precomputed
// once. Def-keyed like homing: split shards (weapon_id suffix ':shard') miss
// the lookup and never inherit the parent's weave.
const WAVE_SPECS = new Map<string, { amplitude: number; period_ticks: number }>();
for (const d of WEAPON_DEFS) {
  if (d.wave) WAVE_SPECS.set(d.id, d.wave);
}

/** Wavy phase step per pool slot: stable per-projectile hash, no RNG. */
const WAVE_PHASE_STEP = Math.PI / 3;

/** Default total volley fan for multi-projectile weapons without spread_deg. */
const DEFAULT_SPREAD_DEG = 10;

const enemyHash = new SpatialHash(cfg.getNumber('perf.max_enemies'));

// Manually fired weapons carry `"manual": true` in content; the flag is not
// part of the shared WeaponDef schema, so it is read through a narrow cast.
function isManualDef(def: WeaponDef): boolean {
  return (def as WeaponDef & { manual?: boolean }).manual === true;
}

interface EffectiveWeaponStats {
  damage: number;
  rate: number;
  speed: number;
  count: number;
  area: number;
  duration: number;
  penetration: number;
  ricochet: number;
  knockback: number;
  range: number;
}

function getWeaponDef(id: string): WeaponDef | undefined {
  return WEAPON_DEFS.find((d) => d.id === id);
}

function effectiveStats(def: WeaponDef, level: number): EffectiveWeaponStats {
  const stats: EffectiveWeaponStats = {
    damage: def.damage,
    rate: def.rate,
    speed: def.speed,
    count: def.count,
    area: def.area,
    duration: def.duration,
    penetration: def.penetration,
    ricochet: def.ricochet,
    knockback: def.knockback,
    range: def.range,
  };

  const levelEntry = def.levels.find((l) => l.level === level);
  if (levelEntry) {
    const e = levelEntry as WeaponLevel;
    if (e.damage !== undefined) stats.damage = e.damage;
    if (e.rate !== undefined) stats.rate = e.rate;
    if (e.speed !== undefined) stats.speed = e.speed;
    if (e.count !== undefined) stats.count = e.count;
    if (e.area !== undefined) stats.area = e.area;
    if (e.duration !== undefined) stats.duration = e.duration;
    if (e.penetration !== undefined) stats.penetration = e.penetration;
    if (e.ricochet !== undefined) stats.ricochet = e.ricochet;
    if (e.knockback !== undefined) stats.knockback = e.knockback;
    if (e.range !== undefined) stats.range = e.range;
  }

  return stats;
}

function len(v: Vec2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

function normalize(v: Vec2): Vec2 {
  const l = len(v);
  if (l < 1e-8) return { x: 0, y: 1 };
  return { x: v.x / l, y: v.y / l };
}

// Fast shortest signed delta across the seam for inputs already within one
// arena of each other. Branch-based; avoids modulo cost in the hit loop.
function fastDelta(a: Vec2, b: Vec2): Vec2 {
  let dx = a.x - b.x;
  let dy = a.y - b.y;
  if (dx > 100) dx -= 200; else if (dx < -100) dx += 200;
  if (dy > 100) dy -= 200; else if (dy < -100) dy += 200;
  return { x: dx, y: dy };
}

function spawnProjectile(
  world: WorldState,
  weapon: WeaponState,
  stats: EffectiveWeaponStats,
  aimPoint: Vec2,
  tick: number,
  angleOffsetRad = 0
): Projectile | null {
  const player = world.player;
  // Seam-aware aim: aimPoint may be raw target coordinates on the far side of
  // the torus seam (up to ~200 u away); shoot along the shortest path instead.
  const dir = normalize(wrappedDelta(aimPoint, player.pos));
  if (angleOffsetRad !== 0) {
    // Spread fan: rotate the aim direction within the volley's total fan.
    const cos = Math.cos(angleOffsetRad);
    const sin = Math.sin(angleOffsetRad);
    const rx = dir.x * cos - dir.y * sin;
    const ry = dir.x * sin + dir.y * cos;
    dir.x = rx;
    dir.y = ry;
  }

  const p = world.projectiles.acquire();
  if (!p) {
    world.events.emit('pool_exhausted', { kind: 'projectile' }, tick);
    return null;
  }

  // Spawn at the hull edge in the fire direction so the shot visibly emerges
  // from the ship even while strafing (ally projectiles never self-collide).
  const muzzle = player.hitbox_radius + 0.3;
  p.pos = { x: player.pos.x + dir.x * muzzle, y: player.pos.y + dir.y * muzzle };
  wrapArena(p.pos);
  p.vel = { x: dir.x * stats.speed, y: dir.y * stats.speed };
  p.hitbox_radius = stats.area;
  p.faction = 'ally';
  p.weapon_id = weapon.def_id;
  p.damage_snapshot = stats.damage;
  p.penetration_left = stats.penetration;
  p.ricochet_left = stats.ricochet;
  p.spawn_tick = tick;
  p.alive = true;
  p.targetable = false;
  p.hit_ids = [];

  const lifetime = stats.range > 0 ? stats.range / stats.speed : stats.duration;
  p.expire_tick = tick + Math.max(1, Math.ceil(lifetime * 60));

  world.events.emit(
    'spawn',
    { kind: 'projectile', id: p.id, weapon_id: weapon.def_id, pos: p.pos },
    tick
  );

  return p;
}

/** Enemy-bullet flight time: 4 s at 60 Hz (~104 u at boss ring speed). */
const ENEMY_BULLET_LIFETIME_TICKS = 240;

/**
 * Enemy-faction bullet (boss volleys, spitter lobs): a pooled projectile with
 * faction 'enemy' that updateProjectiles flies straight and tests against the
 * player only — it never collides with enemies. Damage is the firing def's
 * contact damage × 0.6, flat (no run-time scaling), resolved once at spawn.
 * Deterministic: direction, speed, and volley angles all come from the caller.
 */
export function spawnEnemyBullet(
  world: WorldState,
  e: Enemy,
  def: EnemyDef,
  dir: Vec2,
  speed: number,
  tick: number
): Projectile | null {
  const p = world.projectiles.acquire();
  if (!p) {
    world.events.emit('pool_exhausted', { kind: 'projectile' }, tick);
    return null;
  }

  // Spawn at the hull edge so the shot visibly emerges from the sprite (and
  // the player-hit test never starts inside the firing enemy's own hitbox).
  const muzzle = e.hitbox_radius + 0.3;
  p.pos = { x: e.pos.x + dir.x * muzzle, y: e.pos.y + dir.y * muzzle };
  wrapArena(p.pos);
  p.vel = { x: dir.x * speed, y: dir.y * speed };
  p.hitbox_radius = 0.3;
  p.faction = 'enemy';
  p.weapon_id = def.id;
  p.damage_snapshot = Math.max(1, Math.floor(def.contact_damage * 0.6 + 0.5));
  p.penetration_left = 1;
  p.ricochet_left = 0;
  p.spawn_tick = tick;
  p.alive = true;
  p.targetable = false;
  p.hit_ids = [];
  p.expire_tick = tick + ENEMY_BULLET_LIFETIME_TICKS;

  world.events.emit(
    'spawn',
    { kind: 'projectile', id: p.id, weapon_id: def.id, pos: p.pos },
    tick
  );

  return p;
}

/**
 * Enemy-bullet hit path: static overlap plus a swept segment against the
 * player hitbox only (mirrors the ally path's overlap/sweep pair). Dash
 * i-frames and the mercy window let the bullet pass through harmlessly; a
 * real hit applies the standard shield/HP pipeline, starts the mercy window,
 * emits `player_hit`, and kills the bullet. No RNG — fully deterministic.
 */
function resolveEnemyBulletVsPlayer(
  world: WorldState,
  p: Projectile,
  prevPos: Vec2,
  nextPos: Vec2,
  tick: number,
  mercyTicks: number
): void {
  const player = world.player;
  if (!player.alive) return;

  // Static overlap at the start of the tick counts as an immediate hit.
  const r = p.hitbox_radius + player.hitbox_radius;
  const d0 = fastDelta(prevPos, player.pos);
  let hit = d0.x * d0.x + d0.y * d0.y <= r * r;
  if (!hit) {
    // Unwrap the player into the bullet's local frame so the sweep follows
    // the shortest path across the seam.
    const rel = fastDelta(player.pos, prevPos);
    const b0: Vec2 = { x: prevPos.x + rel.x, y: prevPos.y + rel.y };
    const t = circleCircleSweep(prevPos, nextPos, p.hitbox_radius, b0, b0, player.hitbox_radius);
    hit = t >= 0 && t <= 1;
  }
  if (!hit) return;

  // Dash i-frames / mercy invincibility: zero damage, the bullet flies on.
  if (player.dash.iframes_until > tick || player.mercy_until > tick) return;

  applyDamageToPlayer(player, p.damage_snapshot, tick);
  player.mercy_until = tick + mercyTicks;
  world.events.emit(
    'player_hit',
    { damage: p.damage_snapshot, source: p.weapon_id, pos: { x: player.pos.x, y: player.pos.y } },
    tick
  );
  world.projectiles.release(p);
}

export function updateWeapons(world: WorldState, tick: number): void {
  // Lumensurge upgrade mods, shared by every auto-firing weapon this tick.
  // Crit chance is not applied here; it flows through playerCombatStats()
  // at hit resolution. Deterministic: getUpgradeMods reads only passives.
  const mods = getUpgradeMods(world.player);
  // Loadout STRIKER trait fire rate, applied the same way as the boon mult.
  const traits = getTraitMods(world.player);
  // Serenity tier-2 stance perk: +25% damage and fire rate while the ship
  // holds still long enough to charge the meter.
  const serenity = getSerenityMods(world);
  // Kinetic Tithe boon: +10% fire rate per rank, but only while the ship
  // flies above 60% of its current top speed (same vMax formula movement.ts
  // uses, so move-speed boons/traits shift the threshold honestly).
  let kineticRateMult = 1;
  if (mods.movingFireRateMult > 0) {
    const player = world.player;
    const vMax =
      cfg.getNumber('player.move_speed') *
      (1 + mods.moveSpeedMult + traits.moveSpeedMult) *
      player.boon_move_speed_mult;
    const speed = Math.hypot(player.vel.x, player.vel.y);
    if (vMax > 0 && speed > 0.6 * vMax) kineticRateMult = 1 + mods.movingFireRateMult;
  }
  for (const weapon of world.player.weapons) {
    const def = getWeaponDef(weapon.def_id);
    if (!def) continue;
    // Manual weapons never auto-fire; they fire via fireManualLaser().
    if (isManualDef(def)) continue;

    const stats = effectiveStats(def, weapon.level);
    stats.rate *= (1 + mods.fireRateMult) * (1 + traits.fireRateMult) * serenity.rateMult * kineticRateMult;
    stats.damage *= serenity.damageMult;
    stats.count += mods.projectileCountBonus;
    // Railgun Coils is the only pierce source: without it, enemies absorb
    // every hit (penetration stays at the def's 1).
    stats.penetration += mods.projectilePierceBonus;
    // Damage and crit chance flow through playerCombatStats (combat.ts) so all
    // weapons, including the manual laser, get exactly one application.

    const target = evaluateTarget(weapon, world, tick);
    if (target) {
      weapon.last_target_id = target.targetId;
    } else {
      weapon.last_target_id = -1;
    }

    if (weapon.last_target_id > 0 && tick >= weapon.cooldown_until) {
      const aimPoint = target ? target.aimPoint : world.player.pos;
      const count = Math.max(1, Math.floor(stats.count));
      // Spread (spec-arsenal): volleys with count > 1 fan projectiles
      // symmetrically around the aim direction — the def's spread_deg total
      // fan when set, else a 10° default (lance L5 reads as two bolts).
      const spreadRad = ((def.spread_deg ?? DEFAULT_SPREAD_DEG) * Math.PI) / 180;
      for (let i = 0; i < count; i++) {
        const offset = count > 1 ? ((i - (count - 1) / 2) / (count - 1)) * spreadRad : 0;
        spawnProjectile(world, weapon, stats, aimPoint, tick, offset);
      }
      const cooldownTicks = Math.max(1, Math.round(60 / stats.rate));
      weapon.cooldown_until = tick + cooldownTicks;
    }
  }
}

// Nearest targetable enemy within `range` of the player, seam-aware, with
// deterministic ties (shortest distance, then lowest entity id).
function nearestTargetableEnemy(world: WorldState, tick: number, range: number): Enemy | null {
  let best: Enemy | null = null;
  let bestDistSq = range * range;
  for (const enemy of world.enemies.alive()) {
    if (!enemy.alive || !enemy.targetable) continue;
    if (enemy.telegraph_until > tick) continue;
    const d = wrappedDelta(enemy.pos, world.player.pos);
    const distSq = d.x * d.x + d.y * d.y;
    if (distSq > bestDistSq + 1e-12) continue;
    if (best && Math.abs(distSq - bestDistSq) <= 1e-12 && enemy.id > best.id) continue;
    best = enemy;
    bestDistSq = distSq;
  }
  return best;
}

/**
 * Fire the manual turret (weapon slot 1) once, if it is off cooldown.
 *
 * Aim priority: the pointer tap/click direction when given (the manual
 * turret shoots where the user tapped, independently of the auto weapons),
 * then the current Focus Aim direction while held, then a predicted shot at
 * the nearest targetable enemy in range, then the last Focus Aim direction.
 * Uses the shared projectile pool and the weapon's own cooldown.
 * Deterministic: reads only world state, tick, and the sampled tap direction.
 *
 * Returns true when a volley was fired.
 */
export function fireManualLaser(world: WorldState, tick: number, tapDir?: Vec2 | null): boolean {
  const weapon = world.player.weapons.find((w) => w.slot === 1);
  if (!weapon) return false;
  const def = getWeaponDef(weapon.def_id);
  if (!def || !isManualDef(def)) return false;
  if (tick < weapon.cooldown_until) return false;

  const stats = effectiveStats(def, weapon.level);
  // Apply laser-cannon upgrades, the hold-ground turret bonus, the Serenity
  // tier-2 stance perk, and the loadout STRIKER trait fire rate.
  const mods = getUpgradeMods(world.player);
  const buffs = getTurretWeaponBuffs(world);
  const serenity = getSerenityMods(world);
  const traits = getTraitMods(world.player);
  stats.damage *= (1 + mods.laserDamageMult) * buffs.damageMult * serenity.damageMult;
  stats.rate *= (1 + mods.laserRateMult) * buffs.rateMult * serenity.rateMult * (1 + traits.fireRateMult);
  stats.penetration += mods.laserPierceBonus;
  const player = world.player;
  const aimDir = player.focus_aim.dir;

  let aimPoint: Vec2;
  if (tapDir && (tapDir.x !== 0 || tapDir.y !== 0)) {
    // Pointer tap/click: fire exactly where the user pointed.
    aimPoint = { x: player.pos.x + tapDir.x, y: player.pos.y + tapDir.y };
  } else if (player.focus_aim.held && (aimDir.x !== 0 || aimDir.y !== 0)) {
    aimPoint = { x: player.pos.x + aimDir.x, y: player.pos.y + aimDir.y };
  } else {
    const enemy = nearestTargetableEnemy(world, tick, Math.min(stats.range, world.viewRadius));
    if (enemy) {
      const predicted = predictAim(player.pos, enemy.pos, enemy.vel, stats.speed);
      // Re-anchor across the seam so the shot takes the shortest toroidal path.
      const to = wrappedDelta(predicted, player.pos);
      aimPoint = { x: player.pos.x + to.x, y: player.pos.y + to.y };
    } else if (aimDir.x !== 0 || aimDir.y !== 0) {
      aimPoint = { x: player.pos.x + aimDir.x, y: player.pos.y + aimDir.y };
    } else {
      aimPoint = { x: player.pos.x, y: player.pos.y + 1 };
    }
  }

  const count = Math.max(1, Math.floor(stats.count));
  for (let i = 0; i < count; i++) {
    spawnProjectile(world, weapon, stats, aimPoint, tick);
  }
  weapon.cooldown_until = tick + Math.max(1, Math.round(60 / stats.rate));
  return true;
}

/**
 * Hunter's Mark (seeking boon) hook: the boon wiring attaches an optional
 * player-level homing bonus via setPlayerHomingBonus(); updateProjectiles
 * then grants every ally projectile mild homing. Read duck-typed off the
 * player (mirrors the `haste` field in movement.ts and the `manual` def flag
 * above) so upgrade-system.ts stays untouched until the boon lands.
 */
export function getPlayerHomingBonus(player: Player): HomingSpec | null {
  const spec = (player as unknown as { boonHoming?: HomingSpec }).boonHoming;
  if (!spec) return null;
  if (typeof spec.turn_rate !== 'number' || typeof spec.seek_radius !== 'number') {
    return null;
  }
  return spec;
}

/** Attach (spec) or clear (null) the player-level homing bonus. */
export function setPlayerHomingBonus(player: Player, spec: HomingSpec | null): void {
  const p = player as unknown as { boonHoming?: HomingSpec };
  if (spec) {
    p.boonHoming = { turn_rate: spec.turn_rate, seek_radius: spec.seek_radius };
  } else {
    delete p.boonHoming;
  }
}

/**
 * Homing (spec-arsenal): rotate the projectile's velocity toward the nearest
 * targetable, telegraph-elapsed enemy within seek_radius by at most
 * turn_rate deg/s. Reuses this tick's enemy spatial hash; deterministic ties
 * break to the lowest entity id. Straight-line behavior otherwise unchanged.
 */
function applyHoming(
  p: Projectile,
  homing: HomingSpec,
  dt: number,
  tick: number,
  getPos: (id: number) => Vec2 | undefined,
  enemyMap: Map<number, Enemy>
): void {
  const candidateIds: number[] = [];
  enemyHash.forEachInCircle(p.pos, homing.seek_radius, getPos, (id) => candidateIds.push(id), p.id);

  let best: Enemy | null = null;
  let bestDistSq = homing.seek_radius * homing.seek_radius;
  for (const id of candidateIds) {
    const e = enemyMap.get(id);
    if (!e || !e.alive || e.id !== id) continue;
    if (!e.targetable || e.telegraph_until > tick) continue;
    const d = fastDelta(e.pos, p.pos);
    const distSq = d.x * d.x + d.y * d.y;
    if (distSq > bestDistSq + 1e-12) continue;
    if (best && Math.abs(distSq - bestDistSq) <= 1e-12 && e.id > best.id) continue;
    best = e;
    bestDistSq = distSq;
  }
  if (!best) return;

  const to = fastDelta(best.pos, p.pos);
  const desiredAngle = Math.atan2(to.y, to.x);
  const curAngle = Math.atan2(p.vel.y, p.vel.x);
  let delta = desiredAngle - curAngle;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta < -Math.PI) delta += 2 * Math.PI;
  const maxDelta = ((homing.turn_rate * Math.PI) / 180) * dt;
  const applied = Math.max(-maxDelta, Math.min(maxDelta, delta));
  const speed = len(p.vel);
  p.vel.x = Math.cos(curAngle + applied) * speed;
  p.vel.y = Math.sin(curAngle + applied) * speed;
}

/**
 * Split Rounds: a projectile that dies on an enemy bursts into a fan of shard
 * projectiles — half damage, shorter life, and never re-splitting. The fan is
 * centered on the incoming direction; angles derive from the shard count, so
 * no RNG is needed and replays stay identical.
 */
function spawnSplitShards(world: WorldState, p: Projectile, shardCount: number, tick: number): void {
  if (shardCount <= 0) return;
  if (p.weapon_id.endsWith(':shard')) return; // shards never re-split
  const baseAngle = Math.atan2(p.vel.y, p.vel.x);
  const speed = Math.hypot(p.vel.x, p.vel.y) * 1.05;
  const fanStep = (30 * Math.PI) / 180;
  for (let i = 0; i < shardCount; i++) {
    const s = world.projectiles.acquire();
    if (!s) {
      world.events.emit('pool_exhausted', { kind: 'projectile' }, tick);
      return;
    }
    const angle = baseAngle + (i - (shardCount - 1) / 2) * fanStep;
    s.pos = { x: p.pos.x, y: p.pos.y };
    s.vel = { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
    s.hitbox_radius = Math.max(0.15, p.hitbox_radius * 0.7);
    s.faction = 'ally';
    s.weapon_id = `${p.weapon_id}:shard`;
    s.damage_snapshot = Math.max(1, Math.round(p.damage_snapshot * 0.5));
    s.penetration_left = 1;
    s.ricochet_left = 0;
    s.spawn_tick = tick;
    s.alive = true;
    s.targetable = false;
    s.hit_ids = [];
    s.expire_tick = tick + 30; // 0.5 s shard life
    world.events.emit(
      'spawn',
      { kind: 'projectile', id: s.id, weapon_id: s.weapon_id, pos: s.pos },
      tick
    );
  }
}

export function updateProjectiles(world: WorldState, dt: number, tick: number): void {
  // Split Rounds shard count for this tick (0 = no splitting).
  const splitShards = (() => {
    const bonus = getUpgradeMods(world.player).bulletSplitBonus;
    return bonus > 0 ? 1 + bonus : 0;
  })();
  // Hunter's Mark boon: player-level homing bonus granted to every ally
  // projectile this tick (null when the boon was never taken).
  const homingBonus = getPlayerHomingBonus(world.player);
  // Mercy window granted to the player when an enemy bullet connects (same
  // grace as enemy contact damage).
  const mercyTicks = Math.ceil(cfg.getNumber('player.mercy_window') * cfg.getNumber('sim.step_rate_hz'));
  // Rebuild the enemy spatial hash for this tick.
  enemyHash.clear();
  const enemyMap = new Map<number, Enemy>();
  for (const enemy of world.enemies.alive()) {
    enemyHash.insert(enemy);
    enemyMap.set(enemy.id, enemy);
  }

  const getPos = (id: number): Vec2 | undefined => enemyMap.get(id)?.pos;

  for (const p of world.projectiles.alive()) {
    // Homing is an ally-weapon trait; enemy bullets always fly straight.
    // A def's own homing wins when its turn rate is at least the boon
    // bonus's; otherwise (weaker def spec, or a non-homing def) the bonus
    // steers the shot.
    let homing: HomingSpec | undefined;
    if (p.faction === 'ally') {
      const defHoming = HOMING_SPECS.get(p.weapon_id);
      homing = defHoming;
      if (homingBonus && (!defHoming || defHoming.turn_rate < homingBonus.turn_rate)) {
        homing = homingBonus;
      }
    }
    if (homing) {
      applyHoming(p, homing, dt, tick, getPos, enemyMap);
    }
    const prevPos: Vec2 = { x: p.pos.x, y: p.pos.y };
    // Unwrapped endpoint for swept tests; the stored position wraps so
    // projectiles cross the seam instead of dying at the old arena edge.
    const nextPos: Vec2 = { x: prevPos.x + p.vel.x * dt, y: prevPos.y + p.vel.y * dt };
    p.pos.x = nextPos.x;
    p.pos.y = nextPos.y;

    // Wavy flight (boon-system-v1): weave sinusoidally perpendicular to the
    // current flight direction, lateral offset A·sin(2π·age/period + φ) with
    // φ from pool_index. Applied as the per-tick offset increment after
    // straight integration, so a straight shot traces exactly
    // line + perp·A·sin(ω·age+φ), and homing composes cleanly (homing
    // rotates vel, wave shifts position — they don't fight). nextPos gets
    // the same shift, so the swept segment prevPos→nextPos follows the true
    // wavy path and hit detection stays swept-safe.
    const wave = p.faction === 'ally' ? WAVE_SPECS.get(p.weapon_id) : undefined;
    if (wave && wave.period_ticks > 0) {
      const age = tick - p.spawn_tick;
      const omega = (2 * Math.PI) / wave.period_ticks;
      const phase = p.pool_index * WAVE_PHASE_STEP;
      const dOffset =
        wave.amplitude *
        (Math.sin(omega * age + phase) - Math.sin(omega * (age - 1) + phase));
      if (dOffset !== 0) {
        const speed = len(p.vel);
        if (speed > 1e-8) {
          const perpX = -p.vel.y / speed;
          const perpY = p.vel.x / speed;
          p.pos.x += perpX * dOffset;
          p.pos.y += perpY * dOffset;
          nextPos.x += perpX * dOffset;
          nextPos.y += perpY * dOffset;
        }
      }
    }
    wrapArena(p.pos);

    if (tick >= p.expire_tick) {
      world.projectiles.release(p);
      continue;
    }

    // Enemy-faction bullets (boss volleys, spitter lobs) test against the
    // player only — they pass through enemies untouched.
    if (p.faction === 'enemy') {
      resolveEnemyBulletVsPlayer(world, p, prevPos, nextPos, tick, mercyTicks);
      continue;
    }

    // Broadphase: cover the largest enemy hitbox in content (bulwarks and
    // bosses are big) plus the projectile's own motion this tick. The wave
    // offset can push the endpoint up to the amplitude past the straight
    // integration, so pad the reach accordingly (safe overestimate).
    const motion = len(p.vel) * dt + (wave ? wave.amplitude : 0);
    const queryRadius = p.hitbox_radius + MAX_ENEMY_HITBOX + motion;

    const candidateIds: number[] = [];
    enemyHash.forEachInCircle(p.pos, queryRadius, getPos, (id) => candidateIds.push(id), p.id);
    if (candidateIds.length === 0) continue;

    const hits: { id: number; t: number }[] = [];
    for (const eid of candidateIds) {
      const e = enemyMap.get(eid);
      // Stale-id guard: killEnemy may have released this slot and reused it
      // for a split mini earlier this tick; the hash still holds the old id.
      if (!e || !e.alive || e.id !== eid) continue;
      if (p.hit_ids.includes(eid)) continue;

      // Static overlap at the start of the tick counts as an immediate hit.
      const d0 = fastDelta(prevPos, e.pos);
      const r = p.hitbox_radius + e.hitbox_radius;
      if (d0.x * d0.x + d0.y * d0.y <= r * r) {
        hits.push({ id: eid, t: 0 });
        continue;
      }

      // Unwrap the enemy into the projectile's local frame so the sweep
      // follows the shortest path across the seam.
      const rel = fastDelta(e.pos, prevPos);
      const b0: Vec2 = { x: prevPos.x + rel.x, y: prevPos.y + rel.y };
      const t = circleCircleSweep(
        prevPos,
        nextPos,
        p.hitbox_radius,
        b0,
        b0,
        e.hitbox_radius
      );
      if (t >= 0 && t <= 1) {
        hits.push({ id: eid, t });
      }
    }

    if (hits.length === 0) continue;

    hits.sort((a, b) => {
      if (Math.abs(a.t - b.t) > 1e-6) return a.t - b.t;
      return a.id - b.id;
    });

    for (const hit of hits) {
      if (p.penetration_left <= 0) break;

      const e = enemyMap.get(hit.id);
      if (!e || !e.alive || e.id !== hit.id || p.hit_ids.includes(e.id)) continue;

      const result = resolveHit(p.damage_snapshot, playerCombatStats(world.player), 0, world.rng);
      e.hp -= result.damage;
      p.hit_ids.push(e.id);
      p.penetration_left--;

      world.events.emit(
        'hit',
        {
          projectile_id: p.id,
          target_id: e.id,
          damage: result.damage,
          crit: result.crit,
          weapon_id: p.weapon_id,
          pos: { x: e.pos.x, y: e.pos.y },
        },
        tick
      );

      if (e.hp <= 0) {
        killEnemy(world, e, tick);
      }

      if (p.penetration_left <= 0) {
        // Split Rounds: dying on an enemy bursts into shards.
        spawnSplitShards(world, p, splitShards, tick);
        world.projectiles.release(p);
        break;
      }
    }
  }
}
